import { useEffect, useState } from 'react'
import type { SearchRequest, Filter } from '@medplum/core'
import { formatSearchQuery } from '@medplum/core'
import { useNavigate, useLocation } from 'react-router-dom'
import type { AvailResourceType, AvailResource } from './fhirSearchConfig.ts'
import { fhirSearchConfigs } from './fhirSearchConfig.ts'

export function useFhirSearch<T extends AvailResource>(
  resourceType: T['resourceType'],
  defaultCount: number = 50,
  defaultSort: string = '_lastUpdated',
) {
  const navigate = useNavigate()
  const location = useLocation()

  const [search, setSearch] = useState<SearchRequest<T>>({
    resourceType: resourceType,
    count: defaultCount,
    sortRules: defaultSort.split(',').map((item) => ({
      code: item,
      descending: true,
    })),
    offset: 0,
  })
  const [isInit, setIsInit] = useState(false)
  const [funcQueue, setFuncQueue] = useState<((s: SearchRequest<T>) => void)[]>([])

  const config = fhirSearchConfigs[resourceType]

  if (!config) {
    throw new Error(`Unsupported resource type: ${resourceType}`)
  }

  useEffect(() => {
    const defaultFields = getDefaultFields(resourceType)
    setSearch({
      ...search,
      fields: defaultFields,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!funcQueue.length || search.fields === undefined) return
    for (const func of funcQueue) {
      func(search)
    }
    setFuncQueue([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    // First fire after mount — adopt the URL state as authoritative. Don't
    // call `navigate(...)` with the empty `getSearchQuery()` output (which
    // would strip any query params already in the URL). The internal search
    // state hasn't been hydrated yet on the very first fire (the sibling
    // `useEffect` at line 34 sets `search.fields` on mount, which then
    // re-fires this effect with a meaningful `searchQuery`). Until then,
    // letting the URL win avoids clobbering external state on remount —
    // notably the bounce-back the chart was hitting when a different route
    // navigated away while this hook's component was still in the unmount
    // transition.
    if (!isInit) {
      setIsInit(true)
      return
    }

    const searchQuery = getSearchQuery()

    if (location.search !== searchQuery) {
      navigate(location.pathname + searchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, search])

  const updateStorageFields = (resourceType: AvailResourceType, fields: string[]) => {
    const defaultFieldsString = localStorage.getItem('defaultFields')

    if (!defaultFieldsString) {
      localStorage.setItem('defaultFields', JSON.stringify({ [resourceType]: fields }))
      return
    }

    let fieldsInStorage = JSON.parse(defaultFieldsString)
    const neededFields = fieldsInStorage[resourceType]

    if (
      neededFields !== undefined &&
      !neededFields?.some(
        (field: string) => !config.availableFields.map((item) => item.code).includes(field),
      )
    ) {
      fieldsInStorage[resourceType] = fields
      localStorage.setItem('defaultFields', JSON.stringify(fieldsInStorage))
    } else {
      if (typeof fieldsInStorage !== 'object') {
        fieldsInStorage = {}
      }

      fieldsInStorage[resourceType] = fields
      localStorage.setItem('defaultFields', JSON.stringify(fieldsInStorage))
    }
  }

  const getDefaultFields = (resourceType: AvailResourceType): string[] => {
    const defaultFieldsString = localStorage.getItem('defaultFields')

    try {
      const fieldsInStorage = JSON.parse(defaultFieldsString || '{}')
      const neededFields = fieldsInStorage[resourceType]
      if (
        neededFields !== undefined &&
        !neededFields?.some(
          (field: string) => !config.availableFields.map((item) => item.code).includes(field),
        )
      ) {
        return neededFields
      } else {
        throw new Error('Fields not found')
      }
    } catch {
      updateStorageFields(resourceType, config.defaultFields)
      return config.defaultFields
    }
  }

  const setSort = (sort: string, order: 'asc' | 'desc' | 'none' = 'desc') => {
    const cleanedSearch = {
      ...search,
      offset: 0,
      sortRules: [],
    }
    if (order === 'none') {
      setSearch(cleanedSearch)
      return
    }
    if (config.availableSorting.includes(sort)) {
      setSearch({
        ...cleanedSearch,
        sortRules: [{ code: sort, descending: order === 'desc' }],
      })
    }
  }

  const setFields = (fields: string[]) => {
    if (fields.join(',') !== search.fields?.join(',')) {
      updateStorageFields(resourceType, fields)
      const itemFields = getDefaultFields(resourceType)

      const func = (s: SearchRequest<T>) =>
        setSearch({
          ...s,
          fields: itemFields,
        })
      if (search.fields === undefined) {
        setFuncQueue([...funcQueue, func])
      } else {
        func(search)
      }
    }
  }

  const getSearchQuery = () => {
    if (!isInit) return ''
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fields, ...rest } = search
    const mappedRest =
      config.sortMapping && rest.sortRules?.length
        ? {
            ...rest,
            sortRules: rest.sortRules.map((rule) => ({
              ...rule,
              code: config.sortMapping![rule.code] ?? rule.code,
            })),
          }
        : rest
    return formatSearchQuery(mappedRest)
  }

  const getAvailableFields = () => {
    return config.availableFields
  }

  const getAvailableSorting = () => {
    return config.availableSorting
  }

  const loadMore = () => {
    const func = (s: SearchRequest<T>) =>
      setSearch({
        ...s,
        offset: (search.offset || 0) + (search.count || defaultCount),
      })
    if (search.fields === undefined) {
      setFuncQueue([...funcQueue, func])
    } else {
      func(search)
    }
  }

  const setFilters = (filters: Filter[]) => {
    const func = (s: SearchRequest<T>) =>
      setSearch({
        ...s,
        offset: 0,
        filters: filters,
      })
    if (search.fields === undefined) {
      setFuncQueue([...funcQueue, func])
    } else {
      func(search)
    }
  }

  const setFilter = (code: string, operator: string, value: string) => {
    const currentFilters = search.filters || []
    const filterIndex = currentFilters.findIndex((filter) => filter.code === code)

    if (filterIndex >= 0) {
      const newFilters = [...currentFilters]
      newFilters[filterIndex] = { code, operator, value } as Filter
      setFilters(newFilters)
    } else {
      setFilters([...currentFilters, { code, operator, value } as Filter])
    }
  }

  const removeFilter = (code: string) => {
    const currentFilters = search.filters || []
    if (!currentFilters.some((filter) => filter.code === code)) return
    setFilters(currentFilters.filter((filter) => filter.code !== code))
  }
  const dropOffset = () => {
    search.offset = 0
    setSearch({ ...search })
  }

  return {
    search,
    dropOffset,
    getSearchQuery,
    getAvailableFields,
    getAvailableSorting,
    loadMore,
    setSort,
    setFields,
    setFilters,
    setFilter,
    removeFilter,
  }
}
